/**
 * The one read primitive this feature uses, and the place TanStack Query goes.
 *
 * adr/0007 rules TanStack Router + Query for the web apps. Neither is installed in `web/authors`
 * yet (`@tanstack/react-query` is absent from the workspace's node_modules — only `react-table`
 * and `store` are there), so this is the smallest honest stand-in: load, abort on unmount, expose
 * loading/error/ready and a reload that actually reloads. Every hook in `./` is written against
 * THIS shape, so replacing the body with `useQuery` is one file rather than thirty call sites.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: cache across components, dedupe in flight, or retry. Those are
 * the reasons to want TanStack Query, and pretending to have them here would be the worse outcome
 * — a half-cache that goes stale silently.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type AsyncStatus = 'loading' | 'ready' | 'error';

export interface AsyncResource<T> {
  readonly status: AsyncStatus;
  /** Present once a load has succeeded. It SURVIVES a later failure, so a refresh error can be */
  /** shown beside the data the person was already reading rather than replacing it with a blank. */
  readonly data: T | undefined;
  readonly error: unknown;
  /** True while a reload runs over data that is already on screen. */
  readonly isRefreshing: boolean;
  readonly reload: () => void;
  /** Replaces the held value without a round trip — for a response a mutation already returned. */
  readonly set: (value: T) => void;
}

/**
 * `key`: a value the read depends on beyond the loader itself - the cabinet's language, for a list
 * whose names come in it. A new key reads again, over what is on screen, as a reload does.
 */
export function useAsyncResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  key?: string,
): AsyncResource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [isRefreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);

  // The loader identity changes on every render of the caller, which would loop. Hold the latest
  // one in a ref and depend on the nonce instead.
  const loadRef = useRef(load);
  loadRef.current = load;

  // Whether anything has loaded yet, held in a ref rather than derived from `data`. Deriving it
  // would make it a dependency of the effect below, and a dependency that flips the moment the
  // first load lands is a second, pointless request on every mount.
  const hasDataRef = useRef(false);
  hasDataRef.current = data !== undefined;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    if (hasDataRef.current) setRefreshing(true);
    else setStatus('loading');

    loadRef
      .current(controller.signal)
      .then((value) => {
        if (cancelled) return;
        setData(value);
        setError(undefined);
        setStatus('ready');
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // An abort is our own unmount, never a failure to report.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(cause);
        setStatus('error');
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // ONLY `nonce` and `key`. A reload is asked for explicitly, or by a new key; nothing else
    // re-triggers a read.
  }, [nonce, key]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  const set = useCallback((value: T) => {
    setData(value);
    setError(undefined);
    setStatus('ready');
  }, []);

  return { status, data, error, isRefreshing, reload, set };
}
